






























import { Component, Mixins } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';
import Checkout from '~/components/Store/Checkout.vue';
import OrderGameItemProductMutationV2 from '~/mutations/order/orderProductV2.gql';
import OrderGameItemProductMutation from '~/mutations/orderGameItemProduct.gql';
import orderProductWithW3wMutation from '~/mutations/order/orderProductWithExternalWallet.gql';
import { ICurrentPage } from '~/store/types';
import TwoFactorAuthInputPrompt from '~/components/ModalPrompts/TwoFactorAuthInputPrompt.vue';
import { AuthQuery } from '~/mixins';
import { TwoFaCheckpoint } from '~/types/two-fa-checkpoints';
import { gamesIntlHead } from '~/utils/i18n_head';
import { ITrackPurchaseItemEventPayload } from '~/plugins/userAttribution/types';
import { IPurchaseAgreement } from '../../../store/games/types';
import LegalAgreements, {
  ILegalAgreementsPayload,
  defaultLegalAgreementsPayload,
} from '~/components/LegalAgreements/LegalAgreements.vue';
import { getParsedCookie } from '~/utils';
import { IExternalWalletOrderResponse } from '~/types/gala-store';

@Component({
  head: gamesIntlHead(),
  components: {
    Checkout,
    TwoFactorAuthInputPrompt,
    LegalAgreements,
  },
})
export default class BuyItem extends Mixins(AuthQuery) {
  @Mutation updateCurrentPage!: (payload: ICurrentPage) => void;
  productCategory = '';
  legalAgreementsPayload: ILegalAgreementsPayload = defaultLegalAgreementsPayload;
  w3wConnectionEnabled = process.env.w3wConnectionEnabled;
  galaTagName: string | null = null;
  galaTagBaseIds = [
    'gala-tag-3-month',
    'gala-tag-1-year',
    'gala-tag-10-year',
    'gala-tag-25-year',
    'gala-tag-100-year',
  ];

  get isGalaTagToken() {
    const baseId = this.$route.params?.id;
    if (baseId && this.galaTagBaseIds.includes(baseId)) {
      return true;
    }

    return false;
  }

  get showBsgLegalAgreements() {
    return this.productCategory === 'Battlestar Galactica';
  }

  get allRequiredDocumentsAccepted(): ILegalAgreementsPayload['allAccepted'] {
    return this.legalAgreementsPayload.allAccepted;
  }

  get documentsAccepted(): ILegalAgreementsPayload['documentsAccepted'] {
    return this.legalAgreementsPayload.documentsAccepted;
  }

  get continueButtonsDisabled() {
    if (this.showBsgLegalAgreements) {
      return !this.allRequiredDocumentsAccepted;
    }

    return false;
  }

  get agreements(): IPurchaseAgreement[] {
    if (
      (this.$route.params.id || '').toLowerCase() !==
      '0x031c00000000000000000000000000000000'
    ) {
      return [];
    }

    return [
      {
        label: this.$t(
          'components.legalAgreements.fuzzleAgreement.label',
        ).toString(),
        link: this.$t(
          'components.legalAgreements.fuzzleAgreement.link',
        ).toString(),
        checked: false,
        content: this.$t(
          'components.legalAgreements.fuzzleAgreement.agreement',
        ).toString(),
      },
    ];
  }

  get orderMutation() {
    return this.w3wConnectionEnabled
      ? {
          name: 'orderProductV2',
          mutation: OrderGameItemProductMutationV2,
        }
      : {
          name: 'orderProduct',
          mutation: OrderGameItemProductMutation,
        };
  }

  updateProductCategory(category: string) {
    this.productCategory = category;
  }

  created() {
    this.updateCurrentPage({
      title: 'Buy Item',
      backIcon: 'arrow_back_ios',
    });
  }

  async mounted() {
    await this.$recaptcha.init();

    if (
      this.$route.query?.selectedName &&
      typeof this.$route.query.selectedName === 'string'
    ) {
      const queryGalaTagName = this.$route.query.selectedName;
      const sanitizedName = queryGalaTagName.replace(/[^a-zA-Z0-9_]/g, '');
      this.galaTagName = sanitizedName;
    }

    // If it's a GalaTag token, but for some reason there is no selected name query, we can't generate a galaTagName value.
    // This is unlikely to happen, but if it does somehow happen, the user will need to go though the name selection process again
    if (this.isGalaTagToken && !this.galaTagName) {
      this.$router.push('/gala-tag');
    }
  }

  async beforeDestroy() {
    await this.$recaptcha.destroy();
  }

  async getBotToken() {
    try {
      return await this.$recaptcha.execute('buyItem');
    } catch (err) {
      return '';
    }
  }

  async buyItem(
    passcode: string,
    productId: string,
    paymentCurrency: string,
    quantity: number,
    transactionFeePrice: number,
    expectedPrice: number,
    priceAdjustmentCode?: string,
  ) {
    const results = await this.doAuthQuery(async totpToken => {
      const payload: any = {
        paymentCurrency: paymentCurrency.replace('[ETH]', ''),
        productId,
        walletPassword: passcode,
        quantity,
        priceAdjustmentCode,
        transactionFeePrice: transactionFeePrice.toString(),
        expectedPrice,
        acceptedDocuments: this.documentsAccepted.map(
          ({ documentType, versionAccepted }) => ({
            documentType,
            versionAccepted,
          }),
        ),
        totpToken,
        botToken: await this.getBotToken(),
        orderContext: getParsedCookie('utmVariables') || undefined,
      };

      if (this.galaTagName && this.isGalaTagToken) {
        payload.productContext = {
          nameToken: this.galaTagName,
        };
      }

      const results = await this.$apollo.mutate({
        mutation: this.orderMutation.mutation,
        variables: payload,
      });

      // UA tracking
      const trackPurchaseItemEventPayload: ITrackPurchaseItemEventPayload = {
        total: payload.expectedPrice,
        currency: payload.paymentCurrency,
        item: {
          productId: payload.productId,
          quantity: payload.quantity,
          unitPrice: payload.expectedPrice,
          transactionFee: payload.transactionFeePrice,
        },
        priceAdjustmentCode,
        orderId: results.data[this.orderMutation.name].orderId,
      };

      this.$ua.trackPurchaseItemEvent(trackPurchaseItemEventPayload);

      return results;
    }, TwoFaCheckpoint.purchases);

    return results;
  }

  async placeW3wOrder(
    productId: string,
    paymentCurrency: string,
    quantity: number,
    transactionFeePrice: number,
    expectedPrice: number,
    priceAdjustmentCode?: string,
  ) {
    return this.doAuthQuery(async totpToken => {
      const payload: any = {
        paymentCurrency: paymentCurrency.replace('[ETH]', ''),
        productId,
        quantity,
        transactionFeePrice: transactionFeePrice.toString(),
        expectedPrice,
        acceptedDocuments: this.documentsAccepted.map(
          ({ documentType, versionAccepted }) => ({
            documentType,
            versionAccepted,
          }),
        ),
        totpToken,
        botToken: await this.getBotToken(),
        priceAdjustmentCode,
      };

      if (this.galaTagName && this.isGalaTagToken) {
        payload.productContext = {
          nameToken: this.galaTagName,
        };
      }

      const {
        data: { orderProductWithExternalWallet },
      } = (await this.$apollo.mutate({
        mutation: orderProductWithW3wMutation,
        variables: payload,
      })) as {
        data: { orderProductWithExternalWallet: IExternalWalletOrderResponse };
      };

      // UA tracking
      const trackPurchaseItemEventPayload: ITrackPurchaseItemEventPayload = {
        total: orderProductWithExternalWallet.paymentData?.orderPaymentMessage
          ?.amount as string,
        currency: payload.paymentCurrency,
        item: {
          productId: payload.productId,
          quantity: payload.quantity,
          unitPrice: payload.expectedPrice,
          transactionFee: payload.transactionFeePrice,
        },
        orderId: orderProductWithExternalWallet?.paymentData
          ?.orderPaymentMessage.orderId as string,
        priceAdjustmentCode,
      };

      this.$ua.trackPurchaseItemEvent(trackPurchaseItemEventPayload);

      return orderProductWithExternalWallet;
    }, TwoFaCheckpoint.purchases);
  }
}
